import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../client";

/**
 * Numeric mirrors of Scr.Enums. The number is the representation end to end — the stores persist it
 * and the API serialises it, there being no JsonStringEnumConverter — so these must match the
 * package exactly. Numbers there are identity and append-only; never renumber.
 */
export enum CommunicationCategory {
  Unknown = 0,
  AccountAndSecurity = 1,
  Subscriptions = 2,
  StrategyRequests = 3,
  ProductUpdates = 4,
  Offers = 5,
  Signals = 6,
}

export enum CommunicationChannel {
  Unknown = 0,
  Email = 1,
}

export interface CommunicationPreference {
  category: CommunicationCategory;
  channel: CommunicationChannel;
  /** False where the message is part of the service and cannot be switched off. */
  optional: boolean;
  /** Whether they receive it. Always true when `optional` is false. */
  allowed: boolean;
}

interface CommunicationPreferencesResponse {
  preferences: CommunicationPreference[];
}

const PREFERENCES_KEY = ["communication-preferences"] as const;

export function useCommunicationPreferences() {
  return useQuery<CommunicationPreference[]>({
    queryKey: PREFERENCES_KEY,
    queryFn: async () => {
      const res = await apiClient.get<CommunicationPreferencesResponse>(
        "/customers/me/communication-preferences",
      );
      return res.data.preferences;
    },
    retry: false,
  });
}

export function useUpdateCommunicationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    /**
     * Sends the whole set, not what changed — anything left out is turned off. An empty array is a
     * real request meaning "nothing optional", so it must not be short-circuited.
     */
    mutationFn: async (
      chosen: { category: CommunicationCategory; channel: CommunicationChannel }[],
    ) => {
      const res = await apiClient.put<CommunicationPreferencesResponse>(
        "/customers/me/communication-preferences",
        { preferences: chosen },
      );
      return res.data.preferences;
    },
    // Seeded from the response rather than invalidated: the save answers with the resulting set, so
    // refetching would ask a question we have just been told the answer to — and the answer includes
    // anything the save dropped, which is not derivable from what was sent.
    onSuccess: (preferences) => {
      queryClient.setQueryData<CommunicationPreference[]>(PREFERENCES_KEY, preferences);
    },
  });
}
